import React, { useState, useLayoutEffect, useRef, useCallback } from "react";
import { View, Pressable, ActivityIndicator, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { router, useGlobalSearchParams, useRootNavigationState } from "expo-router";
import { useTheme } from "@/hooks/useTheme";
import { ClientType } from "@/types/db/clients";
import { RelatedProjectsScreen } from "@/components/screens/shared/RelatedProjectsScreen";
import { RelatedShiftsScreen } from "@/components/screens/shared/RelatedShiftsScreen";
import { RelatedContactsScreen } from "@/components/screens/shared/RelatedContactsScreen";
import { RelatedBannedUsersScreen } from "@/components/screens/shared/RelatedBannedUsersScreen";
import { RelatedPreferredUsersScreen } from "@/components/screens/shared/RelatedPreferredUsersScreen";
import { RelatedInvoicesScreen } from "@/components/screens/shared/RelatedInvoicesScreen";
import { RelatedEstimatesScreen } from "@/components/screens/shared/RelatedEstimatesScreen";
import { RelatedIncidentsScreen } from "@/components/screens/shared/RelatedIncidentsScreen";
import { ClientLocationsScreen } from "./ClientLocationsScreen";
import { SmallToggle } from "@/components/SmallToggle";
import DrawerNavigator, { Drawer } from "@/components/screens/shared/DrawerNavigator";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useToolTip } from "@/hooks/useToolTip";
import { ThemedText } from "@/components/ThemedText";

export interface ClientRelatedTabProps {
  client: ClientType | null;
  loading?: boolean;
}

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  tooltipXOffset?: number;
}

function HeaderButton({ icon, tooltip, onPress, tooltipXOffset = -100 }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
          {tooltip}
        </ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x + tooltipXOffset, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip, tooltipXOffset]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name={icon} size={24} color={colors.primary} />
    </Pressable>
  );
}

// Wrapper component for Projects screen with header toggle
function ProjectsScreenWithHeader() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const navigation = useNavigation();
  const [showCompleted, setShowCompleted] = useState(true);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.projects?.plural || "Projects",
      headerRight: () => (
        <View style={{ marginRight: 16 }}>
          <SmallToggle value={showCompleted} onValueChange={setShowCompleted} label="Show Completed" />
        </View>
      ),
    });
  }, [navigation, showCompleted, colors, displayNames]);

  return <RelatedProjectsScreen showInactive={showCompleted} />;
}

// Wrapper component for Shifts screen (no toggle - show all shifts for client)
function ShiftsScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.shifts?.plural || "Shifts",
    });
  }, [navigation, displayNames]);

  return <RelatedShiftsScreen showInactive={true} />;
}

// Wrapper for Banned Users screen with header "+" button
function BannedUsersScreenWithHeader() {
  const navigation = useNavigation();
  const currentEntity = useCurrentEntity();
  const { displayNames } = useAuth();
  const { clients: clientPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: `Banned ${displayNames?.users?.plural || "Users"}`,
      headerRight: () =>
        clientPermissions.update ? <HeaderButton icon="plus" tooltip="Add Banned User" onPress={handleAdd} /> : null,
    });
  }, [navigation, clientPermissions.update, displayNames, handleAdd]);

  return <RelatedBannedUsersScreen clientId={currentEntity?.currentId || ""} externalAddTrigger={addTrigger} />;
}

// Wrapper for Preferred Users screen with header "+" button
function PreferredUsersScreenWithHeader() {
  const navigation = useNavigation();
  const currentEntity = useCurrentEntity();
  const { displayNames } = useAuth();
  const { clients: clientPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: `Preferred ${displayNames?.users?.plural || "Users"}`,
      headerRight: () =>
        clientPermissions.update ? <HeaderButton icon="plus" tooltip="Add Preferred User" onPress={handleAdd} /> : null,
    });
  }, [navigation, clientPermissions.update, displayNames, handleAdd]);

  return <RelatedPreferredUsersScreen clientId={currentEntity?.currentId || ""} externalAddTrigger={addTrigger} />;
}

// Wrapper for Contacts screen with header "+" button
function ContactsScreenWithHeader() {
  const navigation = useNavigation();
  const { displayNames } = useAuth();
  const { clients: clientPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.contacts?.plural || "Contacts",
      headerRight: () =>
        clientPermissions.update ? (
          <HeaderButton icon="plus" tooltip="Add Contact" onPress={handleAdd} tooltipXOffset={-70} />
        ) : null,
    });
  }, [navigation, clientPermissions.update, displayNames, handleAdd]);

  return <RelatedContactsScreen externalAddTrigger={addTrigger} />;
}

// Wrapper for Locations screen with header "+" button
function LocationsScreenWithHeader() {
  const navigation = useNavigation();
  const { clients: clientPermissions } = usePermissions();
  const [addTrigger, setAddTrigger] = useState(0);

  const handleAdd = useCallback(() => {
    setAddTrigger((prev) => prev + 1);
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Locations",
      headerRight: () =>
        clientPermissions.update ? <HeaderButton icon="plus" tooltip="Add Location" onPress={handleAdd} /> : null,
    });
  }, [navigation, clientPermissions.update, handleAdd]);

  return <ClientLocationsScreen externalPickerTrigger={addTrigger} />;
}

// Wrapper for Invoices screen
function InvoicesScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.invoices?.plural || "Invoices",
    });
  }, [navigation, displayNames]);

  return <RelatedInvoicesScreen />;
}

// Wrapper for Estimates screen
function EstimatesScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.estimates?.plural || "Estimates",
    });
  }, [navigation, displayNames]);

  return <RelatedEstimatesScreen />;
}

// Wrapper for Incidents screen
function IncidentsScreenWithHeader() {
  const { displayNames } = useAuth();
  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({
      title: displayNames?.incidents?.plural || "Incidents",
    });
  }, [navigation, displayNames]);

  return <RelatedIncidentsScreen />;
}

export function ClientRelatedTab({ client, loading = false }: ClientRelatedTabProps) {
  const { loading: authLoading, displayNames } = useAuth();
  const { projects, shifts, contacts, users, clients, invoices, estimates, incidents } = usePermissions();
  const { colors } = useTheme();
  const params = useGlobalSearchParams<{ related?: string }>();
  const rootNavigationState = useRootNavigationState();

  // Wait for auth to load before rendering navigator
  if (authLoading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Build available screens based on permissions
  const availableScreens: string[] = [];
  if (projects.read) availableScreens.push("Projects");
  if (shifts.read) availableScreens.push("Shifts");
  if (contacts.read) availableScreens.push("Contacts");
  if (clients.read) availableScreens.push("Locations");
  if (users.read) {
    availableScreens.push("Banned Users");
    availableScreens.push("Preferred Users");
  }
  if (invoices.read) availableScreens.push("Invoices");
  if (estimates.read) availableScreens.push("Estimates");
  if (incidents.read) availableScreens.push("Incidents");

  // Validate requested route exists
  const getValidInitialRoute = (requested?: string): string | undefined => {
    if (!requested) return undefined;
    return availableScreens.find((s) => s.toLowerCase() === requested.toLowerCase());
  };

  const handleScreenChange = (screenName: string) => {
    // Only set params after Root Layout is mounted
    if (!rootNavigationState?.key) return;
    router.setParams({ related: screenName });
  };

  return (
    <DrawerNavigator
      initialRouteName={getValidInitialRoute(params.related)}
      onScreenChange={handleScreenChange}
      entityType="client"
    >
      {projects.read && (
        <Drawer.Screen
          name="Projects"
          component={ProjectsScreenWithHeader}
          options={{ title: displayNames?.projects?.plural || "Projects" }}
        />
      )}
      {shifts.read && (
        <Drawer.Screen
          name="Shifts"
          component={ShiftsScreenWithHeader}
          options={{ title: displayNames?.shifts?.plural || "Shifts" }}
        />
      )}
      {contacts.read && <Drawer.Screen name="Contacts" component={ContactsScreenWithHeader} />}
      {clients.read && <Drawer.Screen name="Locations" component={LocationsScreenWithHeader} />}
      {users.read && <Drawer.Screen name="Banned Users" component={BannedUsersScreenWithHeader} />}
      {users.read && <Drawer.Screen name="Preferred Users" component={PreferredUsersScreenWithHeader} />}
      {invoices.read && (
        <Drawer.Screen
          name="Invoices"
          component={InvoicesScreenWithHeader}
          options={{ title: displayNames?.invoices?.plural || "Invoices" }}
        />
      )}
      {estimates.read && (
        <Drawer.Screen
          name="Estimates"
          component={EstimatesScreenWithHeader}
          options={{ title: displayNames?.estimates?.plural || "Estimates" }}
        />
      )}
      {incidents.read && (
        <Drawer.Screen
          name="Incidents"
          component={IncidentsScreenWithHeader}
          options={{ title: displayNames?.incidents?.plural || "Incidents" }}
        />
      )}
    </DrawerNavigator>
  );
}
